/**
 * Pure reducer + helpers for the Daily Rewards screen (SNAKE-17).
 *
 * No DOM, no timers — these all live in the screen class. The reducer is
 * exercised by unit tests without jsdom.
 */

export interface DailyRewardStatus {
  rewardSchedule: number[]
  activeStreakDays: number
  previousStreakDays: number
  lastClaimedDayIndex: number | null
  nextClaimDayIndex: number
  nextReward: number
  canClaim: boolean
  claimedToday: boolean
  streakContinues: boolean
  resetRequired: boolean
  graceWindow: boolean
  /** ISO string from server (Date is not directly transportable over JSON). */
  nextRewardAvailableAt: string | null
  /** ISO string from server. */
  lastDailyReward: string | null
}

export type DailyRewardsState =
  | { kind: 'loading' }
  | { kind: 'loaded'; status: DailyRewardStatus; lastClaimReward?: number }
  | { kind: 'claiming'; status: DailyRewardStatus }
  | { kind: 'error' }
  | { kind: 'unauthenticated' }

export const initialDailyRewardsState: DailyRewardsState = { kind: 'loading' }

export type DailyRewardsAction =
  | { type: 'load' }
  | { type: 'load_ok'; status: DailyRewardStatus }
  | { type: 'load_err' }
  | { type: 'load_unauth' }
  | { type: 'claim_start' }
  | { type: 'claim_ok'; status: DailyRewardStatus; reward: number }
  /**
   * The server returned `Already claimed today` plus a fresh status — typically
   * because another tab won the race. Silently refresh without a toast.
   */
  | { type: 'claim_already'; status: DailyRewardStatus }
  | { type: 'claim_err' }

export function reduceDailyRewards(
  state: DailyRewardsState,
  action: DailyRewardsAction,
): DailyRewardsState {
  switch (action.type) {
    case 'load':
      return { kind: 'loading' }
    case 'load_ok':
      return { kind: 'loaded', status: action.status }
    case 'load_err':
      return { kind: 'error' }
    case 'load_unauth':
      return { kind: 'unauthenticated' }
    case 'claim_start':
      // Can only fire from a loaded state. If state is something else
      // (defensive), pass through.
      if (state.kind !== 'loaded') return state
      return { kind: 'claiming', status: state.status }
    case 'claim_ok':
      return {
        kind: 'loaded',
        status: action.status,
        lastClaimReward: action.reward,
      }
    case 'claim_already':
      // Quiet refresh: no `lastClaimReward` (we didn't actually claim).
      return { kind: 'loaded', status: action.status }
    case 'claim_err':
      // Drop back to a loaded state if we have one; otherwise error.
      if (state.kind === 'claiming') {
        return { kind: 'loaded', status: state.status }
      }
      return { kind: 'error' }
    default:
      return state
  }
}

// ---------------------------------------------------------------------------
// buildCardStates — 7 cards keyed by their slot index in the cycle
// ---------------------------------------------------------------------------

export type CardState =
  | 'past_claimed'
  | 'today_claimable'
  | 'today_claimed'
  | 'future'

/**
 * Given a `DailyRewardStatus`, return the visual state of each of the seven
 * day cards (index 0..6). Reads the explicit booleans `resetRequired` and
 * `streakContinues` from the DTO instead of deriving them locally.
 */
export function buildCardStates(status: DailyRewardStatus): CardState[] {
  const cards: CardState[] = new Array(7).fill('future')

  // Reset: a previous streak has lapsed (3+ days). Show only Day 1 as
  // claimable; the rest are future. UI shows a "С возвращением" banner.
  if (status.resetRequired) {
    cards[0] = 'today_claimable'
    return cards
  }

  // Never-claimed user (no streak history, no reset). Same shape as reset
  // but without the banner.
  if (!status.streakContinues && status.activeStreakDays === 0) {
    cards[0] = 'today_claimable'
    return cards
  }

  if (status.claimedToday && status.lastClaimedDayIndex !== null) {
    // Past days = past_claimed; today's slot = today_claimed.
    for (let i = 0; i < status.lastClaimedDayIndex; i++) {
      cards[i] = 'past_claimed'
    }
    cards[status.lastClaimedDayIndex] = 'today_claimed'
    return cards
  }

  // canClaim with streak alive (or grace, or wrap after Day 7).
  if (status.canClaim && status.streakContinues) {
    const next = status.nextClaimDayIndex
    // For wrap (next=0 with active streak), there are no past-claimed slots
    // *in this cycle* — Day 1 is the only claimable slot, others are future.
    for (let i = 0; i < next; i++) {
      cards[i] = 'past_claimed'
    }
    cards[next] = 'today_claimable'
    return cards
  }

  return cards
}
